/**
 * Global search box with debounced suggestions.
 *
 * The request is cancelled when the query changes, so a slow response for an
 * earlier keystroke can never overwrite results for a newer one.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@chordbookpro/api-client';
import { api } from '../../services/api.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { Spinner } from '../ui/Spinner.jsx';
import styles from './GlobalSearch.module.css';

const RECENT_KEY = 'chordbookpro.recentSearches';
const MAX_RECENT = 6;

function readRecentSearches() {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(stored) ? stored.slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(term) {
  if (!term?.trim()) return;
  try {
    const existing = readRecentSearches().filter((item) => item !== term);
    localStorage.setItem(RECENT_KEY, JSON.stringify([term, ...existing].slice(0, MAX_RECENT)));
  } catch {
    // Storage may be unavailable; recent searches are a convenience only.
  }
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState(readRecentSearches);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const debounced = useDebouncedValue(query, 250);

  const suggestionsQuery = useQuery({
    queryKey: queryKeys.songs.search({ q: debounced, suggestions: true }),
    queryFn: ({ signal }) => api.songs.search({ q: debounced, limit: 6 }, signal),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  /* Close on outside click. */
  useEffect(() => {
    function handlePointerDown(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  /* "/" focuses search, matching the shortcut people expect from web apps. */
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key !== '/') return;
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (typing) return;

      event.preventDefault();
      inputRef.current?.focus();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const submit = useCallback(
    (term) => {
      const value = (term ?? query).trim();
      if (!value) return;

      saveRecentSearch(value);
      setRecent(readRecentSearches());
      setOpen(false);
      inputRef.current?.blur();
      navigate(`/app/search?q=${encodeURIComponent(value)}`);
    },
    [navigate, query],
  );

  const results = suggestionsQuery.data?.data ?? [];
  const showRecent = open && query.trim().length < 2 && recent.length > 0;
  const showResults = open && query.trim().length >= 2;

  return (
    <div className={styles.container} ref={containerRef}>
      <form
        role="search"
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <span className={styles.icon} aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="m13.5 13.5 3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </span>

        <input
          ref={inputRef}
          type="search"
          className={styles.input}
          placeholder="Search songs and artists…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          aria-label="Search songs and artists"
          aria-expanded={showResults || showRecent}
          aria-controls="global-search-results"
          autoComplete="off"
        />

        {suggestionsQuery.isFetching && <Spinner size={16} label="Searching" />}
      </form>

      {(showResults || showRecent) && (
        <div className={styles.dropdown} id="global-search-results" role="listbox">
          {showRecent && (
            <>
              <p className={styles.sectionLabel}>Recent searches</p>
              {recent.map((term) => (
                <button
                  key={term}
                  type="button"
                  className={styles.item}
                  onClick={() => submit(term)}
                  role="option"
                  aria-selected="false"
                >
                  <span className={styles.itemTitle}>{term}</span>
                </button>
              ))}
            </>
          )}

          {showResults && suggestionsQuery.isLoading && (
            <p className={styles.status}>Searching…</p>
          )}

          {showResults && !suggestionsQuery.isLoading && results.length === 0 && (
            <p className={styles.status}>
              No songs match “{query}”. Try a different spelling, or create your own version.
            </p>
          )}

          {showResults &&
            results.map((song) => (
              <button
                key={song._id ?? song.id}
                type="button"
                className={styles.item}
                role="option"
                aria-selected="false"
                onClick={() => {
                  saveRecentSearch(query);
                  setOpen(false);
                  const arrangementId = song.defaultArrangement?._id ?? song.defaultArrangementId;
                  navigate(
                    arrangementId
                      ? `/app/arrangements/${arrangementId}`
                      : `/app/songs/${song._id ?? song.id}`,
                  );
                }}
              >
                <span className={styles.itemTitle}>{song.title}</span>
                {song.artist && <span className={styles.itemMeta}>{song.artist}</span>}
              </button>
            ))}

          {showResults && results.length > 0 && (
            <button type="button" className={styles.viewAll} onClick={() => submit()}>
              See all results for “{query}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
